import { useCallback, useEffect, useRef, useState } from "react";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import api from "@/api";
import { ApiCatalog } from "@/api/catalogs/ApiCatalog";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import useUser from "@/users/useUser";
import getIdentifier from "./getIdentifier";
import getPayload from "./getPayload";
import initializeListeners from "./initializeListeners";
import useImportIfVisible from "./useImportIsVisible";

dayjs.extend(utc);

const TSOPHIEL_URL = process.env["NEXT_PUBLIC_TSOPHIEL_URL"];

const SOCKET_OPEN_STATE = 1;
const SOCKET_CLOSE_NORMAL = 1000;
const RECONNECT_TIMEOUT_LENGTH = 1500;
const ALLOWED_RECONNECT_ATTEMPTS = 5;

interface UseReadingTrackerProps {
  articleLevelId?: number | null | undefined;
  contentId: string | null | undefined;
}

const formatDateTime = (date: Date): string => {
  return dayjs(date).utc().format("YYYY-MM-DD HH:mm:ss ZZ");
};

const useReadingTracker = ({
  articleLevelId,
  contentId,
}: UseReadingTrackerProps) => {
  const { data: user } = useUser();
  const { isImpersonated } = useGetUserProperties() || {};

  const [ifVisible, setIfVisible] = useState();

  // Import ifvisible.js, which is used to track the idle time on the page.
  // We import ifvisible dynamically to avoid an SSR error caused by the library using document.
  useImportIfVisible(setIfVisible);

  const socketFailedRef = useRef(false);
  const fallbackSentRef = useRef(false);
  const sessionStartRef = useRef(new Date());
  const contentIdRef = useRef(contentId);
  const isImpersonatedRef = useRef(isImpersonated);

  // Keep impersonation ref in sync.
  useEffect(() => {
    isImpersonatedRef.current = !!isImpersonated;
  }, [isImpersonated]);

  const sendFallbackPost = useCallback(() => {
    const cId = contentIdRef.current;

    if (
      !socketFailedRef.current ||
      fallbackSentRef.current ||
      isImpersonatedRef.current ||
      !cId
    ) {
      return;
    }

    fallbackSentRef.current = true;

    const sessionEnded = new Date();

    const payload = {
      timeline: [
        { session_started: formatDateTime(sessionStartRef.current) },
        { session_ended: formatDateTime(sessionEnded) },
      ],
      session_started: formatDateTime(sessionStartRef.current),
      platform: "web",
      content: {
        id: cId,
      },
    };

    api()
      .post(ApiCatalog.contentViewingSession.endpoint, payload)
      .catch((error: unknown) => {
        console.error("Reading session fallback POST failed:", error);
      });
  }, []);

  // Reset session state when contentId changes (e.g. student switches lexile level).
  useEffect(() => {
    if (contentIdRef.current === contentId) return;

    // Fire fallback for the previous contentId if the socket was failed.
    sendFallbackPost();

    contentIdRef.current = contentId;
    socketFailedRef.current = false;
    fallbackSentRef.current = false;
    sessionStartRef.current = new Date();
  }, [contentId]);

  // Fallback listeners — fires the fallback POST when the user leaves and
  // the socket is in a failed state. Runs once for the component lifecycle.
  useEffect(() => {
    const handleBeforeUnload = () => {
      sendFallbackPost();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // Component is truly unmounting (SPA navigation away).
      sendFallbackPost();

      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Handle the websocket connection and initialize the event listeners.
  // This effect may re-run when dependencies change (ifVisible loads,
  // user data arrives, etc). It manages socket lifecycle and marks
  // socketFailedRef when all retries exhaust — the actual fallback POST
  // fires from the beforeunload/unmount effect above.
  useEffect(() => {
    if (!articleLevelId && !contentId) return;
    if (isImpersonated) return;

    if (!TSOPHIEL_URL || TSOPHIEL_URL === "undefined") {
      console.warn("TSOPHIEL_URL is undefined.");
      return;
    }

    // Disposed flag: when the effect cleanup runs, this is set to true.
    // This prevents stale socket handlers from a previous effect invocation
    // from running. Critical because closing a CONNECTING socket fires
    // onclose with code 1006 (not 1000), which would otherwise trigger
    // the retry loop from a stale closure.
    let disposed = false;

    let socket: WebSocket;
    let timeStarted = new Date().getTime();
    let reconnectAttempts = 0;
    const pendingTimeouts: NodeJS.Timeout[] = [];

    // Handle initial WebSocket errors (in case it is blocked by district)
    try {
      socket = new WebSocket(TSOPHIEL_URL);
    } catch (error) {
      const errorMessage = "Reading Websocket error: " + JSON.stringify(error);
      console.error(errorMessage);
      socketFailedRef.current = true;
      return;
    }

    const sendDelayedMessage = (additionalProperties: any) => {
      const pendingTimeout = setTimeout(() => {
        if (disposed) return;

        const messageProperties = {
          identifier: getIdentifier(),
          levelId: articleLevelId,
          contentId,
          timeStarted,
          userId: user?.id,
          userUniqueId: user?.unique_id,
        };

        const payload = getPayload(messageProperties, additionalProperties);

        if (socket.readyState === SOCKET_OPEN_STATE) {
          socket.send(payload);
        } else if (reconnectAttempts < ALLOWED_RECONNECT_ATTEMPTS) {
          sendDelayedMessage(additionalProperties);
        }
      }, 250);

      pendingTimeouts.push(pendingTimeout);
    };

    const onSocketOpen = () => {
      if (disposed) return;
      socketFailedRef.current = false;
      fallbackSentRef.current = false;
      reconnectAttempts = 0;
      sendDelayedMessage({});
    };

    const onSocketError = (error: any) => {
      if (disposed) return;
      const errorMessage = "Reading Websocket error: " + JSON.stringify(error);
      console.error(errorMessage);
    };

    const onSocketClose = (event: any) => {
      if (disposed) return;
      if (event.code === SOCKET_CLOSE_NORMAL) {
        return;
      }

      // Attempt to reconnect.
      if (
        socket.readyState !== SOCKET_OPEN_STATE &&
        reconnectAttempts < ALLOWED_RECONNECT_ATTEMPTS
      ) {
        const pendingTimeout = setTimeout(() => {
          if (disposed) return;
          timeStarted = new Date().getTime();
          reconnectAttempts = reconnectAttempts + 1;

          socket = new WebSocket(TSOPHIEL_URL);
          socket.onopen = onSocketOpen;
          socket.onclose = onSocketClose;
          socket.onerror = onSocketError;
        }, RECONNECT_TIMEOUT_LENGTH * reconnectAttempts);

        pendingTimeouts.push(pendingTimeout);
      } else if (reconnectAttempts >= ALLOWED_RECONNECT_ATTEMPTS) {
        // All retries exhausted — mark socket as failed.
        // The fallback POST will fire on page leave (beforeunload/unmount).
        socketFailedRef.current = true;
      }
    };

    socket.onopen = onSocketOpen;
    socket.onclose = onSocketClose;
    socket.onerror = onSocketError;

    const cleanUpListeners = initializeListeners(ifVisible, sendDelayedMessage);

    return () => {
      disposed = true;
      cleanUpListeners();
      pendingTimeouts.forEach((timeout) => clearTimeout(timeout));
      socket.close(SOCKET_CLOSE_NORMAL);
    };
  }, [articleLevelId, contentId, ifVisible, user?.id, isImpersonated]);
};

export default useReadingTracker;
